import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Gradient } from './Components/Gradient';
import { LeftDetails } from './Components/LeftDetails';
import { RightDetails } from './Components/RightDetails';
import { Speed } from './Components/Speed';
import { MiddleMenu } from './Components/MiddleMenu';
import {
  useCalories,
  useElevation,
  useHeartRate,
  useMoves,
  useRunningDistance,
} from './Components/Hooks';

const VADesign = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [seconds, setSeconds] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [speed, setSpeed] = useState(0);

  const [pace, setPace] = useState(0);

  const [gradient, setGradient] = useState(0);

  const { distance, reset: resetRunningDistance } = useRunningDistance({
    isRunning,
    speedKmh: speed,
    initialDistance: 0,
  });

  const { heartRate, reset: resetHeartRate } = useHeartRate({
    isRunning,
    speedKmh: speed,
    restingHR: 68,
    maxHR: 195,
  });

  const { calories, reset: resetCalories } = useCalories({
    isRunning,
    speedKmh: speed,
  });

  const { moves, reset: resetMoves } = useMoves({
    isRunning,
    speedKmh: speed,
  });

  const { elevation, reset: resetElevation } = useElevation({
    isRunning,
    speedKmh: speed,
    gradient,
  });

  const startTimer = () => {
    if (!isRunning) {
      setIsRunning(true);
      if (speed <= 0) setSpeed(1);
    }
  };

  const stopTimer = () => {
    setIsRunning(false);
    resetRunningDistance();
    resetHeartRate();
    setSpeed(0);
    setPace(0);
    setSeconds(0);
  };

  const handleSpeedChange = (value: number) => {
    // if (speed === 0 && value <= 0) return;
    setSpeed((prev) => {
      if (prev + value < 0) return 0;
      return prev + value;
    });
  };

  const handleStaticSpeedChange = (value: number) => {
    setSpeed(value);
  };

  const handleGradientChange = (value: number) => {
    // if (speed === 0 && value <= 0) return;
    setGradient((prev) => {
      if (prev + value < 0) return 0;
      return prev + value;
    });
  };

  const handleStaticGradientChange = (value: number) => {
    setGradient(value);
  };

  // Main timer logic
  useEffect(() => {
    console.log('running isRunning useEffect');
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount or stop
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning]);

  useEffect(() => {
    if (speed > 0) {
      const paceInMinutesPerKm = 60 / speed;
      // Round to 2 decimal places (e.g., 5.45 min/km)
      setPace(Number(paceInMinutesPerKm));
    } else {
      setPace(0);
    }
  }, [speed]);

  return (
    // <Fragment>
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        marginTop: 10,
        //   margin: '0 auto',
        backgroundColor: '#0b111d',
        // backgroundColor: 'red',
        //   color: 'white',
        padding: '20px',
        flex: 1, // This makes it grow to fill remaining space
        minHeight: 0, // Allows it to shrink properly
        overflow: 'auto', // Allows scrolling inside if content is long
        display: 'flex',
        flexDirection: 'column',
        height: '94%',
      }}
    >
      <Grid
        container
        direction="row"
        spacing={2}
        sx={(theme) => ({
          height: '100%',
          maxWidth: 2400,
          minWidth: 1600,
          margin: '0 auto',
        })}
      >
        <Grid item xs={1}>
          <Gradient
            gradient={gradient}
            handleGradientChange={handleGradientChange}
            handleStaticGradientChange={handleStaticGradientChange}
          />
        </Grid>
        <Grid item xs={3}>
          <LeftDetails
            calories={calories}
            moves={moves}
            elevation={elevation}
          />
        </Grid>
        <Grid item xs={4}>
          <MiddleMenu
            seconds={seconds}
            isRunning={isRunning}
            startTimer={startTimer}
            stopTimer={stopTimer}
          />
        </Grid>
        <Grid item xs={3}>
          <RightDetails pace={pace} distance={distance} heartRate={heartRate} />
        </Grid>
        <Grid item xs={1}>
          <Speed
            speed={speed}
            handleSpeedChange={handleSpeedChange}
            handleStaticSpeedChange={handleStaticSpeedChange}
          />
        </Grid>
      </Grid>
    </Box>
    // </Fragment>
  );
};

export default VADesign;
